import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Save, ShieldCheck, KeyRound, ListChecks, Trash2, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import CopyButton from "@/components/admin/CopyButton";

const API = getApiUrl();
const MASK = "••••••••";

/**
 * Admin → Integrations → Zoopla Testing
 * - Manage mTLS credentials + branch reference per environment (sandbox / live)
 * - Trigger real listing/update, listing/delete, listing/list, branch/update
 *   against Zoopla via mTLS and inspect the raw response.
 */
export default function ZooplaTestingSection() {
  const [envConfig, setEnvConfig] = useState({ sandbox: {}, live: {} });
  const [activeEnv, setActiveEnv] = useState("sandbox");
  const [host, setHost] = useState("");
  const [paths, setPaths] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [busyAction, setBusyAction] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [liveListings, setLiveListings] = useState(null); // null | [] | [{listing_reference,url}]
  const [refreshingLive, setRefreshingLive] = useState(false);
  const [removingRef, setRemovingRef] = useState(null); // listing_reference currently being removed
  const [bulkRemoving, setBulkRemoving] = useState(false);

  const BRANCH_DEFAULT_KEYS = [
    "branch_name",
    "branch_telephone",
    "branch_email",
    "branch_website",
    "branch_street_name",
    "branch_town_or_city",
    "branch_postal_code",
    "branch_country_code",
  ];

  // Merge CMS-derived defaults into an env settings object. Only fills blanks,
  // never overwrites values the admin has already set.
  const mergeDefaults = (cur, defaults) => {
    const out = { ...(cur || {}) };
    BRANCH_DEFAULT_KEYS.forEach((k) => {
      const v = defaults?.[k];
      if (v && !((out[k] || "").toString().trim())) out[k] = v;
    });
    return out;
  };

  const load = async () => {
    try {
      setLoading(true);
      const [{ data }, propsResp, defaultsResp] = await Promise.all([
        axios.get(`${API}/admin/integrations/zoopla-settings`, { withCredentials: true }),
        axios.get(`${API}/properties`),
        axios
          .get(`${API}/admin/integrations/zoopla-branch-defaults`, { withCredentials: true })
          .catch(() => ({ data: {} })),
      ]);
      const envs = data.environments || {};
      const defaults = defaultsResp.data || {};
      setEnvConfig({
        sandbox: mergeDefaults(envs.sandbox?.settings, defaults),
        live:    mergeDefaults(envs.live?.settings, defaults),
      });
      setHost(data.host || "");
      setPaths(data.paths || {});
      setActiveEnv(data.active_environment || "sandbox");
      setProperties(propsResp.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Zoopla settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (env, patch) => setEnvConfig((prev) => ({ ...prev, [env]: { ...prev[env], ...patch } }));

  const save = async () => {
    try {
      setSaving(true);
      const cfg = envConfig[activeEnv] || {};
      // Only send fields that aren't the masked placeholder
      const payload = { environment: activeEnv };
      [
        "branch_reference",
        "branch_name",
        "branch_telephone",
        "branch_email",
        "branch_website",
        "branch_street_name",
        "branch_town_or_city",
        "branch_postal_code",
        "branch_country_code",
        "client_cert_pem",
        "client_key_pem",
        "ca_bundle_pem",
      ].forEach((k) => {
        if (cfg[k] !== undefined && cfg[k] !== MASK) payload[k] = cfg[k];
      });
      if (typeof cfg.auto_push_enabled === "boolean") payload.auto_push_enabled = cfg.auto_push_enabled;
      await axios.put(`${API}/admin/integrations/zoopla-settings`, payload, { withCredentials: true });
      toast.success("Zoopla settings saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });

  const onCertUpload = async (env, key, file) => {
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      update(env, { [key]: text });
      toast.success(`${key.replace(/_/g, " ")} loaded (unsaved)`);
    } catch (e) {
      toast.error("Failed to read file");
    }
  };

  const call = async (action) => {
    try {
      setBusyAction(action);
      setLastResult(null);
      const { data } = await axios.post(
        `${API}/admin/integrations/zoopla-test-call`,
        { environment: activeEnv, action, property_id: propertyId || undefined },
        { withCredentials: true, timeout: 90000 }
      );
      setLastResult(data);
      if (data.status === "success") {
        toast.success(`${action} → HTTP ${data.http_status}`);
        // Refresh the "Live on Zoopla" table whenever the state may have changed.
        if (["listing_list", "listing_update", "listing_delete"].includes(action)) {
          refreshLiveListings();
        }
      } else {
        toast.error(data.message || `${action} failed`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || e.message;
      setLastResult({ status: "error", message: msg });
      toast.error(msg || `${action} failed`);
    } finally {
      setBusyAction(null);
    }
  };

  // Fetch the live listings that Zoopla currently has for this branch. Called
  // by the "Refresh" button in the Live-on-Zoopla panel and after any mutation.
  const refreshLiveListings = async () => {
    try {
      setRefreshingLive(true);
      const { data } = await axios.post(
        `${API}/admin/integrations/zoopla-test-call`,
        { environment: activeEnv, action: "listing_list" },
        { withCredentials: true, timeout: 90000 }
      );
      if (data.status === "success") {
        try {
          const parsed = JSON.parse(data.response_text || "{}");
          const rows = Array.isArray(parsed.listings)
            ? parsed.listings.map((l) => ({
                listing_reference: l.listing_reference || "",
                url: l.url || "",
              }))
            : [];
          setLiveListings(rows);
        } catch (_) { setLiveListings([]); }
      }
    } catch (_) { /* ignore — silent refresh */ }
    finally { setRefreshingLive(false); }
  };

  // Remove a single Zoopla listing by its listing_reference — used for the
  // per-row Remove button AND the Remove-All bulk action.
  const removeListingByRef = async (listingRef) => {
    const parts = String(listingRef || "").split("|");
    const propId = parts.length > 1 ? parts.slice(1).join("|") : listingRef;
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/zoopla-test-call`,
        { environment: activeEnv, action: "listing_delete", property_id: propId },
        { withCredentials: true, timeout: 90000 }
      );
      return data.status === "success";
    } catch (_) { return false; }
  };

  const handleRemoveOne = async (listingRef) => {
    if (!window.confirm(`Remove listing ${listingRef} from Zoopla ${activeEnv.toUpperCase()}?`)) return;
    setRemovingRef(listingRef);
    const ok = await removeListingByRef(listingRef);
    setRemovingRef(null);
    if (ok) toast.success(`Removed ${listingRef}`);
    else toast.error(`Failed to remove ${listingRef}`);
    refreshLiveListings();
  };

  const handleRemoveAll = async () => {
    const rows = liveListings || [];
    if (!rows.length) return;
    if (!window.confirm(
      `Remove ALL ${rows.length} listing(s) from Zoopla ${activeEnv.toUpperCase()}?\n\n` +
      `This dispatches listing/delete for each listing reference below and cannot be undone from here. ` +
      `Local CMS records are NOT touched — you can re-push any listing with listing/update.`
    )) return;
    setBulkRemoving(true);
    let done = 0, failed = 0;
    for (const row of rows) {
      const ok = await removeListingByRef(row.listing_reference);
      if (ok) done += 1; else failed += 1;
    }
    setBulkRemoving(false);
    if (failed === 0) toast.success(`Removed all ${done} listing(s) from Zoopla ${activeEnv.toUpperCase()}`);
    else toast.error(`${done} removed, ${failed} failed`);
    refreshLiveListings();
  };

  // Auto-load the live listings whenever the environment changes.
  useEffect(() => {
    if (!loading) refreshLiveListings();
  }, [activeEnv, loading]);

  const cur = envConfig[activeEnv] || {};

  return (
    <div className="space-y-4">
      <IntegrationApiLogToggle provider="zoopla" />
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Zoopla Connection & Testing</CardTitle>
            <CardDescription>
              mTLS to <code className="text-xs">{host || "realtime-listings-api.webservices.zpg.co.uk"}</code> · Schema v2.3 · JSON
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="zoopla-settings-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeEnv} onValueChange={setActiveEnv}>
            <TabsList>
              <TabsTrigger value="sandbox" data-testid="zoopla-env-sandbox">Sandbox</TabsTrigger>
              <TabsTrigger value="live"    data-testid="zoopla-env-live">Live</TabsTrigger>
            </TabsList>
            {["sandbox", "live"].map((env) => (
              <TabsContent key={env} value={env} className="space-y-4 pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Branch Reference (Zoopla Branch ID)</Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_reference || ""}
                      onChange={(e) => update(env, { branch_reference: e.target.value })}
                      placeholder="e.g. 169450"
                      data-testid={`zoopla-branch-ref-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Branch Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_name || ""}
                      onChange={(e) => update(env, { branch_name: e.target.value })}
                      placeholder="e.g. Sell Local Homes"
                      data-testid={`zoopla-branch-name-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Telephone</Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_telephone || ""}
                      onChange={(e) => update(env, { branch_telephone: e.target.value })}
                      placeholder="e.g. 01234 567890"
                      data-testid={`zoopla-branch-tel-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_email || ""}
                      onChange={(e) => update(env, { branch_email: e.target.value })}
                      placeholder="info@selllocalhomes.co.uk"
                      data-testid={`zoopla-branch-email-${env}`}
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label>Website</Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_website || ""}
                      onChange={(e) => update(env, { branch_website: e.target.value })}
                      placeholder="https://selllocalhomes.co.uk"
                      data-testid={`zoopla-branch-website-${env}`}
                    />
                  </div>
                  <div className="md:col-span-2 border-t pt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Branch Location <span className="text-red-500 normal-case font-normal">(required for branch/update)</span></div>
                  </div>
                  <div className="space-y-1">
                    <Label>Street Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_street_name || ""}
                      onChange={(e) => update(env, { branch_street_name: e.target.value })}
                      placeholder="e.g. 123 High Street"
                      data-testid={`zoopla-branch-street-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Town / City <span className="text-red-500">*</span></Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_town_or_city || ""}
                      onChange={(e) => update(env, { branch_town_or_city: e.target.value })}
                      placeholder="e.g. Hoddesdon"
                      data-testid={`zoopla-branch-town-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Postal Code <span className="text-red-500">*</span></Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_postal_code || ""}
                      onChange={(e) => update(env, { branch_postal_code: e.target.value })}
                      placeholder="e.g. EN11 8XX"
                      data-testid={`zoopla-branch-postcode-${env}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Country Code</Label>
                    <Input
                      value={(env === activeEnv ? cur : envConfig[env])?.branch_country_code || "GB"}
                      onChange={(e) => update(env, { branch_country_code: e.target.value })}
                      placeholder="GB"
                      maxLength={2}
                      data-testid={`zoopla-branch-country-${env}`}
                    />
                  </div>
                  <div className="md:col-span-2 border-t pt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">mTLS Credentials</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> Client Certificate (.crt / .pem)</Label>
                    <Input
                      type="file"
                      accept=".crt,.pem,.cer"
                      onChange={(e) => onCertUpload(env, "client_cert_pem", e.target.files?.[0])}
                      data-testid={`zoopla-cert-file-${env}`}
                    />
                    {(env === activeEnv ? cur : envConfig[env])?.client_cert_pem === MASK && (
                      <p className="text-[11px] text-emerald-600 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Certificate stored</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> Private Key (.pem / private.pem)</Label>
                    <Input
                      type="file"
                      accept=".pem,.key"
                      onChange={(e) => onCertUpload(env, "client_key_pem", e.target.files?.[0])}
                      data-testid={`zoopla-key-file-${env}`}
                    />
                    {(env === activeEnv ? cur : envConfig[env])?.client_key_pem === MASK && (
                      <p className="text-[11px] text-emerald-600 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Key stored</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>CA Bundle (optional — for server-cert pinning)</Label>
                    <Input
                      type="file"
                      accept=".crt,.pem"
                      onChange={(e) => onCertUpload(env, "ca_bundle_pem", e.target.files?.[0])}
                      data-testid={`zoopla-ca-file-${env}`}
                    />
                  </div>
                  <div className="md:col-span-2 flex items-start justify-between gap-4 border rounded p-3 bg-muted/20">
                    <div>
                      <Label className="flex items-center gap-1"><Zap className="h-3 w-3" /> Auto-push on property save</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        When enabled, saving any property in the CMS automatically dispatches <code>listing/update</code> to Zoopla {env}. Change-detection means no-op saves are skipped.
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(envConfig[env]?.auto_push_enabled)}
                      onCheckedChange={(v) => update(env, { auto_push_enabled: v })}
                      data-testid={`zoopla-auto-push-${env}`}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={save} disabled={saving || loading} data-testid={`zoopla-save-settings-${env}`}>
                    {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />Save {env}</>}
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Message Dispatcher</CardTitle>
          <CardDescription>
            Send a real mTLS request. Choose the property, pick an action, and inspect Zoopla&apos;s raw response below.
            {" "}
            <a
              href="https://realtime-listings.webservices.zpg.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
              data-testid="zoopla-portal-link"
            >
              Preview submitted listings on the Zoopla Real-Time Listings portal ↗
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Property (for listing/update &amp; listing/delete)</Label>
              <select
                className="w-full h-9 border rounded px-2 text-sm"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                data-testid="zoopla-property-picker"
              >
                <option value="">— select property —</option>
                {properties.map((p) => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.title || p.property_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs text-muted-foreground">
                Environment: <strong className="uppercase">{activeEnv}</strong>
              </div>
            </div>
          </div>

          {/* CTA buttons + inline explanations */}
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 min-w-[140px]"
                onClick={() => call("branch_update")}
                disabled={busyAction !== null}
                data-testid="zoopla-test-branch-update"
              >
                {busyAction === "branch_update" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                branch/update
              </Button>
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong>Registers or updates your branch on Zoopla.</strong> Send this once during onboarding (and whenever your agent details change). Publishes the branch metadata under Branch Reference <code className="text-[11px]">{envConfig[activeEnv]?.branch_reference || "—"}</code> without touching any listings.
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 border rounded-md">
              <Button
                size="sm"
                className="shrink-0 min-w-[140px]"
                onClick={() => call("listing_update")}
                disabled={busyAction !== null || !propertyId}
                data-testid="zoopla-test-listing-update"
              >
                {busyAction === "listing_update" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                listing/update
              </Button>
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong>Creates or updates a single property on Zoopla.</strong> Builds the JSON payload from the property you selected + your Field Mapping tab, then POSTs it to Zoopla. The manual button always sends (bypassing change-detection); auto-push on save will skip if nothing changed since last sync. Returns the new <code className="text-[11px]">ZPG-Listing-ETag</code> which is stored for optimistic concurrency.
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 border rounded-md">
              <Button
                variant="destructive"
                size="sm"
                className="shrink-0 min-w-[140px]"
                onClick={() => call("listing_delete")}
                disabled={busyAction !== null || !propertyId}
                data-testid="zoopla-test-listing-delete"
              >
                {busyAction === "listing_delete" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                listing/delete
              </Button>
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong>Removes a property from Zoopla immediately.</strong> Sends the listing reference (<code className="text-[11px]">branch|property_id</code>) with no payload body. Use this when you take a property off the market or sell it under a channel you don&apos;t want listed on Zoopla. Doesn&apos;t affect your local CMS copy — you can re-push at any time with listing/update.
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 border rounded-md">
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 min-w-[140px]"
                onClick={() => call("listing_list")}
                disabled={busyAction !== null}
                data-testid="zoopla-test-listing-list"
              >
                {busyAction === "listing_list" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListChecks className="mr-2 h-4 w-4" />}
                listing/list
              </Button>
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong>Reads back everything Zoopla thinks your branch has live.</strong> Doesn&apos;t need a property — returns Zoopla&apos;s side of the truth. Handy for reconciling: compare this list against your CMS to spot properties that failed to push, or leftover listings that should have been deleted.
              </div>
            </div>
          </div>

          {/* Live on Zoopla — remove listings that already exist on their side */}
          <div className="border rounded-md p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">
                  Live on Zoopla <span className="text-muted-foreground uppercase">({activeEnv})</span>
                  {liveListings !== null && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      · {liveListings.length} listing{liveListings.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  Every listing Zoopla currently has under this branch reference. Remove any that should no longer be shown.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshLiveListings}
                  disabled={refreshingLive || bulkRemoving}
                  data-testid="zoopla-live-refresh"
                >
                  {refreshingLive ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                  Refresh
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveAll}
                  disabled={!liveListings || liveListings.length === 0 || bulkRemoving || refreshingLive}
                  data-testid="zoopla-live-remove-all"
                >
                  {bulkRemoving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Trash2 className="mr-1 h-3 w-3" />}
                  Remove All
                </Button>
              </div>
            </div>

            {liveListings === null ? (
              <div className="text-xs text-muted-foreground italic p-4 text-center">
                Loading Zoopla listings…
              </div>
            ) : liveListings.length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-4 text-center border rounded border-dashed">
                No listings currently live on Zoopla for this branch.
              </div>
            ) : (
              <ul className="divide-y border rounded">
                {liveListings.map((row) => {
                  const isRemoving = removingRef === row.listing_reference || bulkRemoving;
                  const localMatch = properties.find(
                    (p) => String(row.listing_reference || "").endsWith(`|${p.property_id}`)
                  );
                  return (
                    <li
                      key={row.listing_reference}
                      className="flex items-center gap-3 px-3 py-2 text-xs"
                      data-testid={`zoopla-live-row-${row.listing_reference}`}
                    >
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="font-mono text-[11px] break-all">{row.listing_reference}</div>
                        <div className="text-muted-foreground truncate">
                          {localMatch ? (
                            <span className="text-emerald-700">✓ Matches CMS: {localMatch.title || localMatch.property_id}</span>
                          ) : (
                            <span className="text-amber-700">! No matching CMS property</span>
                          )}
                        </div>
                      </div>
                      {row.url && (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-700 hover:text-emerald-900 underline text-[11px] shrink-0"
                          data-testid={`zoopla-live-preview-${row.listing_reference}`}
                        >
                          Preview ↗
                        </a>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOne(row.listing_reference)}
                        disabled={isRemoving}
                        className="text-red-600 hover:text-red-800 shrink-0"
                        data-testid={`zoopla-live-remove-${row.listing_reference}`}
                      >
                        {removingRef === row.listing_reference
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Trash2 className="h-3 w-3" />
                        }
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {lastResult && (() => {
            // Zoopla returns per-listing preview URLs — parse them out of
            // the JSON body so we can render click-through links.
            let previewLinks = [];
            try {
              const parsed = JSON.parse(lastResult.response_text || "{}");
              if (parsed.url) {
                previewLinks.push({ url: parsed.url, ref: parsed.listing_reference || "listing" });
              }
              if (Array.isArray(parsed.listings)) {
                parsed.listings.forEach((l) => {
                  if (l.url) previewLinks.push({ url: l.url, ref: l.listing_reference || "listing" });
                });
              }
            } catch (_) { /* not JSON — skip */ }
            return (
              <div className="space-y-2 border rounded p-3 bg-muted/30">
                <div className="text-xs font-semibold uppercase text-muted-foreground">
                  {lastResult.status === "success" ? "✔ Success" : "✕ Error"}
                  {lastResult.http_status ? ` — HTTP ${lastResult.http_status}` : ""}
                </div>
                {lastResult.url && <div className="text-[11px] break-all"><span className="text-muted-foreground">Endpoint:</span> <code>{lastResult.url}</code></div>}
                {previewLinks.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-300 rounded p-3 space-y-2">
                    <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                      Preview on Zoopla ({previewLinks.length})
                    </div>
                    <ul className="space-y-1">
                      {previewLinks.map((l, i) => (
                        <li key={i} className="text-xs">
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-700 hover:text-emerald-900 underline font-medium break-all inline-flex items-start gap-1"
                            data-testid={`zoopla-preview-link-${i}`}
                          >
                            <span className="text-[10px] mt-0.5 shrink-0">↗</span>
                            <span>
                              <span className="text-muted-foreground mr-1">{l.ref}:</span>
                              {l.url}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {lastResult.message && <div className="text-xs text-red-600">{lastResult.message}</div>}
                {lastResult.request_body && (
                  <details>
                    <summary className="text-xs cursor-pointer flex items-center justify-between">
                      <span>Request body</span>
                      <CopyButton text={JSON.stringify(lastResult.request_body, null, 2)} testId="zoopla-request-copy" />
                    </summary>
                    <pre className="text-[10px] mt-1 max-h-64 overflow-auto bg-background p-2 rounded">{JSON.stringify(lastResult.request_body, null, 2)}</pre>
                  </details>
                )}
                {lastResult.response_text && (
                  <details open={previewLinks.length === 0}>
                    <summary className="text-xs cursor-pointer flex items-center justify-between">
                      <span>Response body</span>
                      <CopyButton text={lastResult.response_text} testId="zoopla-response-copy" />
                    </summary>
                    <pre className="text-[10px] mt-1 max-h-64 overflow-auto bg-background p-2 rounded whitespace-pre-wrap">{lastResult.response_text}</pre>
                  </details>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
